import "./campaigns.page.css";

import { Link, Navigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { fetchCampaignById, updateCampaignEncounter } from "../../clients/campaigns.client";
import monstersCatalog from "../../catalogs/monstersCatalog.json";
import CenteredContainer from "../../components/layout/CenteredContainer/CenteredContainer";

const catalogByName = new Map(monstersCatalog.entries.map((monster) => [monster.name.ru, monster]));
const catalogById = new Map(monstersCatalog.entries.map((monster) => [monster.id, monster]));

const rollMonsterHp = (hp) => {
  const formula = hp?.match(/\[([^\]]+)]/)?.[1];
  const terms = formula?.match(/[+-]?[^+-]+/g) ?? [];

  return terms.reduce((total, term) => {
    const normalizedTerm = term.replace(/\s/g, "");
    const dice = normalizedTerm.match(/^\+?(\d+)d(\d+)$/i);
    if (!dice) return total + Number(normalizedTerm);

    const [, count, sides] = dice;
    return total + Array.from(
      { length: Number(count) },
      () => Math.floor(Math.random() * Number(sides)) + 1
    ).reduce((sum, roll) => sum + roll, 0);
  }, 0);
};

const abilityModifier = (score) => score != null && Number.isFinite(Number(score))
  ? Math.floor((Number(score) - 10) / 2)
  : 0;

const abilityLabels = {
  strength: "Сила",
  dexterity: "Ловкость",
  constitution: "Выносливость",
  intelligence: "Интеллект",
  wisdom: "Мудрость",
  charisma: "Харизма",
};

const D20Icon = () => (
  <svg aria-hidden="true" className="encounter-d20-icon" viewBox="0 0 24 24">
    <path d="M12 2.5 21 8.8l-3.4 10.5H6.4L3 8.8 12 2.5Z" />
    <path d="m12 2.5 4.2 6.3L12 18.7 7.8 8.8 12 2.5Zm-9 6.3h18M6.4 19.3 12 18.7l5.6.6" />
  </svg>
);

const getMonsterForEnemy = (enemy) => {
  if (!enemy) return null;
  const catalogId = monstersCatalog.entries.find((entry) => enemy.id?.startsWith(`${entry.id}-`))?.id;
  return catalogById.get(enemy.catalogId || catalogId) || catalogByName.get(enemy.name) || null;
};

const EncounterPage = () => {
  const { campaignId, encounterId } = useParams();
  const tokenValue = useSelector((state) => state.auth.tokenValue);
  const [campaign, setCampaign] = useState(null);
  const [encounter, setEncounter] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [selectedEnemyId, setSelectedEnemyId] = useState("");

  useEffect(() => {
    let cancelled = false;
    fetchCampaignById(campaignId, tokenValue).then((response) => {
      if (!cancelled) {
        const nextCampaign = response.isError ? null : response.data;
        setCampaign(nextCampaign);
        setEncounter(nextCampaign?.encounters.find((entry) => entry.id === encounterId) ?? null);
        setLoaded(true);
      }
    });
    return () => { cancelled = true; };
  }, [campaignId, encounterId, tokenValue]);

  if (!loaded) return <div className="campaigns-shell" />;
  if (!campaign || !encounter) return <Navigate replace to={`/campaigns/${campaignId}`} />;

  const selectedEnemy = encounter.enemyEntries.find((enemy) => enemy.id === selectedEnemyId) || encounter.enemyEntries[0];
  const monster = getMonsterForEnemy(selectedEnemy);
  const referenceUrl = monster?.referenceUrl;

  const saveEncounter = async (nextEncounter) => {
    setEncounter(nextEncounter);
    await updateCampaignEncounter(campaignId, encounterId, nextEncounter, tokenValue);
  };

  const updateEnemy = (enemyId, field, value) => {
    const nextEncounter = {
      ...encounter,
      enemyEntries: encounter.enemyEntries.map((enemy) => enemy.id === enemyId ? { ...enemy, [field]: Number(value) || 0 } : enemy),
    };
    saveEncounter(nextEncounter);
  };

  const rollHp = (enemy) => {
    const nextHp = rollMonsterHp(getMonsterForEnemy(enemy)?.statBlock.hp);
    if (!nextHp) return;
    const nextEncounter = { ...encounter, enemyEntries: encounter.enemyEntries.map((entry) => entry.id === enemy.id ? { ...entry, currentHp: nextHp, maxHp: nextHp } : entry) };
    saveEncounter(nextEncounter);
  };

  const rollInitiative = (enemy) => {
    const dexterity = getMonsterForEnemy(enemy)?.statBlock.abilityScores?.dexterity;
    updateEnemy(enemy.id, "initiative", Math.floor(Math.random() * 20) + 1 + abilityModifier(dexterity));
  };

  return <div className="campaigns-shell"><CenteredContainer><main className="campaign-page">
    <header className="campaign-page-header">
      <div><p>Боевой энкаунтер</p><h1>{encounter.title}</h1><span>{encounter.description}</span></div>
      <Link className="campaign-back-link" to={`/campaigns/${campaignId}`}>К кампании</Link>
    </header>
    <div className="encounter-page-grid">
      <section className="campaign-section">
        <div className="campaign-section-header"><div><p>Порядок боя</p><h2>Существа</h2></div></div>
        <div className="encounter-combat-grid">
          {encounter.enemyEntries.map((enemy) => <article className={`encounter-combat-tile${selectedEnemy?.id === enemy.id ? " is-selected" : ""}`} key={enemy.id} onClick={() => setSelectedEnemyId(enemy.id)}>
            <strong>{enemy.name}</strong>
            <div className="encounter-tile-field"><span>Инициатива</span><div className="encounter-inline-value"><input aria-label={`Инициатива: ${enemy.name}`} type="number" value={enemy.initiative ?? ""} onChange={(event) => updateEnemy(enemy.id, "initiative", event.target.value)} /><button aria-label={`Бросить инициативу: ${enemy.name}`} className="encounter-roll-button" title="Бросить инициативу" type="button" onClick={(event) => { event.stopPropagation(); rollInitiative(enemy); }}><D20Icon /></button></div></div>
            <div className="encounter-tile-field encounter-tile-hp"><span>HP</span><div className="encounter-inline-value"><input aria-label={`HP: ${enemy.name}`} min="0" type="number" value={enemy.currentHp ?? ""} onChange={(event) => updateEnemy(enemy.id, "currentHp", event.target.value)} /><span>/</span><input aria-label={`Максимум HP: ${enemy.name}`} min="0" type="number" value={enemy.maxHp ?? ""} onChange={(event) => updateEnemy(enemy.id, "maxHp", event.target.value)} /><button aria-label={`Бросить HP: ${enemy.name}`} className="encounter-roll-button" title="Бросить HP" type="button" onClick={(event) => { event.stopPropagation(); rollHp(enemy); }}><D20Icon /></button></div></div>
          </article>)}
        </div>
      </section>
      <aside className="encounter-detail-panel">
        <p>Карточка существа</p>{referenceUrl ? <h2><a className="encounter-monster-link" href={referenceUrl} target="_blank" rel="noreferrer">{selectedEnemy?.name}</a></h2> : <h2>{selectedEnemy?.name || "Выберите существо"}</h2>}
        {monster ? <>
          <div className="encounter-detail-stats"><span>HP: {monster.statBlock.hp}</span><span>Скорость: {monster.statBlock.speed}</span></div>
          {monster.statBlock.abilityScores && <section><h3>Характеристики</h3><div className="encounter-ability-grid">{Object.entries(abilityLabels).map(([ability, label]) => <span key={ability}><small>{label}</small><strong>{monster.statBlock.abilityScores[ability] ?? "-"}</strong></span>)}</div></section>}
          {monster.statBlock.attacks.melee.length > 0 && <section><h3>Атаки</h3>{monster.statBlock.attacks.melee.map((attack) => <p key={attack}>{attack}</p>)}</section>}
          {monster.statBlock.traits.length > 0 && <section><h3>Черты</h3><p>{monster.statBlock.traits.join(", ")}</p></section>}
          {referenceUrl && <a className="encounter-monster-link" href={referenceUrl} target="_blank" rel="noreferrer">Открыть в Pathfinder Family</a>}
        </> : <p>Подробная карточка появится после привязки существа к каталогу.</p>}
      </aside>
    </div>
    <div className="encounter-support-grid">
      <section className="campaign-section"><p>Заметки мастера</p><h2>Заметки</h2><textarea className="encounter-edit-field" value={encounter.notes} onChange={(event) => setEncounter({ ...encounter, notes: event.target.value })} onBlur={() => saveEncounter(encounter)} /></section>
      <section className="campaign-section"><p>После победы</p><h2>Награды</h2><label>Опыт<input className="encounter-edit-field" min="0" type="number" value={encounter.experienceReward} onChange={(event) => setEncounter({ ...encounter, experienceReward: Number(event.target.value) || 0 })} onBlur={() => saveEncounter(encounter)} /></label><textarea className="encounter-edit-field" value={(encounter.rewardItems ?? []).join("\n")} onChange={(event) => setEncounter({ ...encounter, rewardItems: event.target.value.split("\n").filter(Boolean) })} onBlur={() => saveEncounter(encounter)} /></section>
    </div>
  </main></CenteredContainer></div>;
};

export default EncounterPage;
